'use client'

import {
    NotifiableEntityType,
    NotificationChannel,
    NotificationPreferences,
    readChannels,
    updateNotificationPreferences,
} from '@/lib/api/common/notification-preferences-api'
import { useEffect, useState } from 'react'
import { MdEmail, MdNotificationsActive, MdNotificationsOff, MdWhatsapp } from 'react-icons/md'
import { MdOutlineNotifications } from 'react-icons/md'
import { toast } from 'react-toastify'

interface NotificationPreferencesCardProps {
    entityType: NotifiableEntityType
    entityId: number
    /** O valor gravado, vindo do registro que a tela já carregou. */
    preferences?: NotificationPreferences | null
    /** Nome de quem recebe — usado nos textos, para a decisão ficar concreta. */
    entityName?: string
    /** Sem permissão de edição, o cartão vira só leitura. */
    canEdit?: boolean
    className?: string
}

const CHANNELS: {
    key: NotificationChannel
    label: string
    icon: typeof MdEmail
    hint: string
}[] = [
    {
        key: 'email',
        label: 'E-mail',
        icon: MdEmail,
        hint: 'Agendamentos, lembretes, cobranças e repasses por e-mail',
    },
    {
        key: 'whatsapp',
        label: 'WhatsApp',
        icon: MdWhatsapp,
        hint: 'As mesmas mensagens pelo WhatsApp cadastrado',
    },
    {
        key: 'inApp',
        label: 'Aviso no sistema',
        icon: MdOutlineNotifications,
        hint: 'Notificações dentro do painel, para quem acessa o portal',
    },
]

/**
 * Por quais canais esta entidade aceita ser notificada.
 *
 * **Tudo nasce desligado e só liga quem consentiu** — é a razão de existir do
 * recurso: parceiros e profissionais reclamaram de receber aviso a cada agendamento.
 * O mesmo componente serve parceiro, profissional, fornecedor e hospital; só o
 * caminho da API muda.
 *
 * Fica na tela de **detalhes**, não na de edição, de propósito: é uma decisão de
 * relacionamento tomada quando se está olhando o cadastro, não um campo de formulário
 * que se preenche junto com endereço e documento — e que exigiria salvar a tela
 * inteira para valer.
 */
export default function NotificationPreferencesCard({
    entityType,
    entityId,
    preferences,
    entityName,
    canEdit = true,
    className = '',
}: NotificationPreferencesCardProps) {
    const [channels, setChannels] = useState(() => readChannels(preferences))
    const [saving, setSaving] = useState<NotificationChannel | null>(null)

    // O pai recarrega a entidade depois de outras ações; o cartão acompanha.
    useEffect(() => {
        setChannels(readChannels(preferences))
    }, [preferences])

    const anyOn = CHANNELS.some(channel => channels[channel.key])

    const toggle = async (channel: NotificationChannel) => {
        const next = !channels[channel]
        // Otimista: o toggle responde na hora e volta atrás se a chamada falhar —
        // esperar o servidor para pintar um switch faz a tela parecer travada.
        setChannels(prev => ({ ...prev, [channel]: next }))
        setSaving(channel)

        try {
            const res = await updateNotificationPreferences(entityType, entityId, {
                [channel]: next,
            })
            if (!res.success) throw new Error(res.error || 'falha')
            toast.success(
                next
                    ? 'Canal ativado. As notificações voltam a ser enviadas por aqui.'
                    : 'Canal desativado. Nada mais será enviado por este meio.'
            )
        } catch {
            setChannels(prev => ({ ...prev, [channel]: !next }))
            toast.error('Não foi possível salvar a preferência')
        } finally {
            setSaving(null)
        }
    }

    const consentAt = preferences?.consentAt

    return (
        <div
            className={`bg-base-100 rounded-xl border border-base-300/50 p-6 ${className}`}
        >
            <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
                <h2 className="text-lg font-semibold text-base-content flex items-center gap-2">
                    {anyOn ? (
                        <MdNotificationsActive className="text-xl text-success" />
                    ) : (
                        <MdNotificationsOff className="text-xl text-base-content/40" />
                    )}
                    Notificações
                </h2>
                <span
                    className={`badge badge-sm ${anyOn ? 'badge-success' : 'badge-ghost'}`}
                >
                    {anyOn ? 'Recebendo' : 'Não recebe'}
                </span>
            </div>

            <p className="text-sm text-base-content/60 mb-4">
                {anyOn
                    ? `${entityName || 'Este cadastro'} recebe avisos pelos canais marcados abaixo.`
                    : `${entityName || 'Este cadastro'} não recebe nenhum aviso. Ative um canal apenas se houver consentimento.`}
            </p>

            <div className="flex flex-col gap-3">
                {CHANNELS.map(channel => {
                    const Icon = channel.icon
                    const active = channels[channel.key]
                    return (
                        <label
                            key={channel.key}
                            className={`flex items-start gap-3 rounded-lg border p-3 ${
                                active
                                    ? 'border-success/40 bg-success/5'
                                    : 'border-base-300/70'
                            } ${canEdit ? 'cursor-pointer' : ''}`}
                        >
                            <input
                                type="checkbox"
                                className="toggle toggle-success toggle-sm mt-0.5"
                                checked={active}
                                disabled={!canEdit || saving === channel.key}
                                onChange={() => toggle(channel.key)}
                            />
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-base-content flex items-center gap-1.5">
                                    <Icon className="text-base" />
                                    {channel.label}
                                </div>
                                <div className="text-xs text-base-content/50">
                                    {channel.hint}
                                </div>
                            </div>
                        </label>
                    )
                })}
            </div>

            {/* Quem desliga precisa saber o que deixa de chegar: cobrança e repasse
                saem pelos mesmos canais, e a surpresa apareceria semanas depois, na
                forma de "não fui avisado do meu pagamento". */}
            <p className="mt-4 text-xs text-base-content/50">
                Com o canal desligado, nada é enviado por ele — inclusive avisos de
                cobrança e de repasse. O acesso à conta continua funcionando: código de
                acesso ao portal e redefinição de senha são sempre enviados.
            </p>

            {consentAt && (
                <p className="mt-2 text-xs text-base-content/40">
                    Consentimento registrado em{' '}
                    {new Date(consentAt).toLocaleString('pt-BR')}.
                </p>
            )}
        </div>
    )
}
